import { useEffect, useMemo, useState } from 'react';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ApiError } from '../../lib/api-client';
import { CkEditorField } from '../../components/ui/CkEditorField';
import { confirmAction, confirmBillUpdate } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import { useCancelIpdBill, useIpdBill, useUpdateIpdBill } from '../ipd-daycare/ipd-daycare.queries';
import { billSecondaryPaymentModes, currency, formatIpdDateTime, freshPaymentRows, toDateTimeInput } from '../ipd-daycare/ipd-daycare.utils';
import type { IpdBillPaymentInput } from '../ipd-daycare/types';
import { useDialysisLookup } from './dialysis.queries';
import { FieldLabel, LookupSelect } from './dialysis.shared';
import type { DialysisAdmission } from './types';

interface DraftLine { id?: string; charge: string; name: string; doctor: string | null; date: string; rate: number; quantity: number; discountPercent: number; discountAmount: number; cancelled: boolean }

const lineAmount = (line: Pick<DraftLine, 'rate' | 'quantity' | 'discountPercent' | 'discountAmount' | 'cancelled'>) => {
  if (line.cancelled) return 0;
  const gross = line.rate * line.quantity;
  return Math.max(0, gross - Math.max(line.discountAmount, gross * line.discountPercent / 100));
};

export const DialysisEditBillForm = ({ admission, billId, onCancelled }: { admission: DialysisAdmission; billId: string; onCancelled: () => void }) => {
  const { showToast } = useToast();
  const query = useIpdBill(billId);
  const charges = useDialysisLookup('charges');
  const doctors = useDialysisLookup('doctors');
  const updateBill = useUpdateIpdBill();
  const cancelBill = useCancelIpdBill();

  const [lines, setLines] = useState<DraftLine[] | null>(null);
  const [billDate, setBillDate] = useState('');
  const [chargeId, setChargeId] = useState('');
  const [draftDoctor, setDraftDoctor] = useState<string | null>(null);
  const [draftRate, setDraftRate] = useState(0);
  const [draftQuantity, setDraftQuantity] = useState(1);
  const [draftDiscountPercent, setDraftDiscountPercent] = useState(0);
  const [draftDiscountAmount, setDraftDiscountAmount] = useState(0);
  const [newPayments, setNewPayments] = useState<IpdBillPaymentInput[]>(freshPaymentRows);
  const [miscChargeType, setMiscChargeType] = useState<'flat' | 'percentage'>('flat');
  const [miscCharge, setMiscCharge] = useState(0);
  const [discountType, setDiscountType] = useState<'flat' | 'percentage'>('flat');
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [financeRemark, setFinanceRemark] = useState('');
  const [departmentPartyRemark, setDepartmentPartyRemark] = useState('');

  useEffect(() => {
    if (!query.data) return;
    setBillDate(toDateTimeInput(query.data.billDate));
    setLines(query.data.charges.map((line) => ({ id: line.id, charge: line.charge, name: line.chargeName, doctor: line.doctor?.id ?? null, date: line.date, rate: line.rate, quantity: line.quantity, discountPercent: line.discountPercent, discountAmount: line.discountAmount, cancelled: line.cancelled })));
    setMiscChargeType(query.data.miscChargeType);
    setMiscCharge(query.data.miscCharge);
    setDiscountType(query.data.discountType);
    setTotalDiscount(query.data.totalDiscount);
    setFinanceRemark(query.data.financeRemark ?? '');
    setDepartmentPartyRemark(query.data.departmentPartyRemark ?? '');
  }, [query.data]);

  const subTotal = useMemo(() => (lines ?? []).reduce((sum, line) => sum + lineAmount(line), 0), [lines]);
  const miscAmount = miscChargeType === 'percentage' ? subTotal * miscCharge / 100 : miscCharge;
  const total = subTotal + miscAmount;
  const discountAmount = discountType === 'percentage' ? total * totalDiscount / 100 : totalDiscount;
  const grandTotal = Math.max(0, total - discountAmount);
  const existingPaid = (query.data?.payments.reduce((sum, payment) => sum + payment.amount, 0) ?? 0) + (query.data?.creditAdjustments.reduce((sum, adjustment) => sum + adjustment.amount, 0) ?? 0);
  const newPaymentTotal = newPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const totalPayment = existingPaid + newPaymentTotal;
  const dueAmount = Math.max(0, grandTotal - totalPayment);
  const creditAmount = Math.max(0, totalPayment - grandTotal);

  const resetDraft = () => { setChargeId(''); setDraftDoctor(null); setDraftRate(0); setDraftQuantity(1); setDraftDiscountPercent(0); setDraftDiscountAmount(0); };
  const chooseCharge = (id: string) => {
    setChargeId(id);
    const selected = charges.data?.find((item) => item.id === id);
    setDraftRate(selected?.rate ?? 0);
  };
  const draftAmount = lineAmount({ rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount, cancelled: false });
  const addLine = () => {
    const selected = charges.data?.find((item) => item.id === chargeId);
    if (!selected || !lines) return;
    setLines([...lines, { charge: selected.id, name: selected.label, doctor: draftDoctor, date: billDate, rate: draftRate, quantity: draftQuantity, discountPercent: draftDiscountPercent, discountAmount: draftDiscountAmount, cancelled: false }]);
    resetDraft();
  };
  const updateLine = (index: number, patch: Partial<DraftLine>) => setLines((current) => current?.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line) ?? current);
  const toggleCancelLine = (index: number) => setLines((current) => current?.map((line, lineIndex) => lineIndex === index ? { ...line, cancelled: !line.cancelled } : line) ?? current);

  const updatePayment = (index: number, patch: Partial<IpdBillPaymentInput>) => setNewPayments((current) => current.map((payment, paymentIndex) => paymentIndex === index ? { ...payment, ...patch } : payment));

  const submit = () => {
    if (!lines) return;
    if (!billDate) {
      showToast('Please select Bill Date before updating.', 'warning');
      return;
    }
    void confirmBillUpdate().then((confirmed) => {
      if (!confirmed) return;
      updateBill.mutate({
        billId,
        payload: {
          billDate,
          charges: lines.map((line) => ({ ...(line.id ? { id: line.id } : {}), charge: line.charge, doctor: line.doctor, date: billDate, rate: line.rate, quantity: line.quantity, discountPercent: line.discountPercent, discountAmount: line.discountAmount, cancelled: line.cancelled })),
          miscChargeType,
          miscCharge,
          discountType,
          totalDiscount,
          newPayments: newPayments.filter((payment) => payment.amount > 0),
          financeRemark: financeRemark.trim() || null,
          departmentPartyRemark: departmentPartyRemark.trim() || null,
        },
      }, {
        onSuccess: () => { setNewPayments(freshPaymentRows()); showToast('Bill updated successfully.', 'success'); },
        onError: (error) => showToast(error instanceof ApiError ? error.message : 'The bill could not be updated.', 'error'),
      });
    });
  };

  const confirmCancel = async () => {
    const confirmed = await confirmAction({
      title: 'Cancel Full Bill?',
      description: 'Are you sure you want to cancel the full bill? This action cannot be undone.',
      confirmLabel: 'Yes, Cancel Bill',
      tone: 'danger',
    });
    if (!confirmed) return;
    cancelBill.mutate(billId, {
      onSuccess: () => { showToast('Bill cancelled successfully.', 'success'); onCancelled(); },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The bill could not be cancelled.', 'error'),
    });
  };

  if (query.isLoading || !lines) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState title="Unable to load bill" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} />;
  const bill = query.data;
  if (bill.status === 'cancelled') return <EmptyState title="This bill is cancelled" message="A cancelled bill cannot be edited." />;

  return (
    <div className="opd-prescription">
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row">
          <div className="opd-bill-banner-fields">
            <span>Bill No : {bill.billNumber}</span>
            <span>Bill Date : {formatIpdDateTime(bill.billDate)}</span>
            <span>Bill Section : {bill.section}</span>
          </div>
        </div>
        <p>Bill Created By - {bill.createdBy.name}{bill.editedBy ? ` || Last Edit By - ${bill.editedBy.name}` : ''}</p>
      </header>

      <section className="opd-section">
        <div className="opd-edit-bill-fields">
          <label><FieldLabel required>Bill Date</FieldLabel><input type="datetime-local" value={billDate} onChange={(event) => setBillDate(event.target.value)} /></label>
        </div>
      </section>

      <section className="opd-section"><h2>Charges</h2>
        <div className="table-scroll">
          <table className="opd-charge-table">
            <thead><tr><th>Charge Name<span className="opd-required">*</span></th><th>Reffer Doctor</th><th>Rate (₹)</th><th className="opd-charge-narrow">#/Cancel</th><th>Qty</th><th>Dis (%)</th><th>Dis Amount (₹)</th><th>Amount (₹)</th></tr></thead>
            <tbody>
              <tr className="opd-draft-row">
                <td><LookupSelect id="dialysis-edit-bill-charge" value={chargeId} options={charges.data} placeholder="Select" onChange={chooseCharge} /></td>
                <td><LookupSelect id="dialysis-edit-bill-doctor" value={draftDoctor ?? ''} options={doctors.data} placeholder="Select" onChange={(value) => setDraftDoctor(value || null)} /></td>
                <td><input type="number" min={0} step="0.01" value={draftRate} onChange={(event) => setDraftRate(Number(event.target.value))} /></td>
                <td className="opd-charge-narrow"><button className="opd-add" type="button" onClick={addLine} disabled={!chargeId}>+</button></td>
                <td><input type="number" min={1} value={draftQuantity} onChange={(event) => setDraftQuantity(Number(event.target.value))} /></td>
                <td><input type="number" min={0} max={100} step="0.01" value={draftDiscountPercent} onChange={(event) => setDraftDiscountPercent(Number(event.target.value))} /></td>
                <td><input type="number" min={0} step="0.01" value={draftDiscountAmount} onChange={(event) => setDraftDiscountAmount(Number(event.target.value))} /></td>
                <td><strong>{currency(draftAmount)}</strong></td>
              </tr>
              {lines.map((line, index) => (
                <tr key={line.id ?? `new-${index}`} style={line.cancelled ? { opacity: 0.6 } : undefined}>
                  <td className="opd-charge-name-cell" title={line.name}>{line.name}</td>
                  <td><LookupSelect id={`dialysis-edit-bill-line-doctor-${index}`} value={line.doctor ?? ''} options={doctors.data} placeholder="Select" disabled={line.cancelled} onChange={(value) => updateLine(index, { doctor: value || null })} /></td>
                  <td><input type="number" min={0} step="0.01" value={line.rate} disabled={line.cancelled} onChange={(event) => updateLine(index, { rate: Number(event.target.value) })} /></td>
                  <td className="opd-charge-narrow">{line.id ? <input type="checkbox" checked={line.cancelled} onChange={() => toggleCancelLine(index)} aria-label={`Cancel ${line.name}`} /> : <button className="opd-cancel" type="button" onClick={() => setLines(lines.filter((_, lineIndex) => lineIndex !== index))}>×</button>}</td>
                  <td><input type="number" min={1} value={line.quantity} disabled={line.cancelled} onChange={(event) => updateLine(index, { quantity: Number(event.target.value) })} /></td>
                  <td><input type="number" min={0} max={100} step="0.01" value={line.discountPercent} disabled={line.cancelled} onChange={(event) => updateLine(index, { discountPercent: Number(event.target.value) })} /></td>
                  <td><input type="number" min={0} step="0.01" value={line.discountAmount} disabled={line.cancelled} onChange={(event) => updateLine(index, { discountAmount: Number(event.target.value) })} /></td>
                  <td><strong>{currency(lineAmount(line))}</strong></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="opd-section opd-payment-section"><h2>New Payment</h2>
        <div className="opd-payment-row">
          <div className="opd-payment-methods">
            <div className="opd-payment-grid">
              <div><label>Payment Mode<select value="Cash" disabled><option>Cash</option></select></label><label>Payment Amount<input type="number" min={0} step="0.01" value={newPayments[0]?.amount ?? 0} onChange={(event) => updatePayment(0, { amount: Number(event.target.value) })} /></label></div>
            </div>
            <div className="opd-payment-grid">
              <div><label>Mode<select value={newPayments[1]?.mode ?? 'UPI'} onChange={(event) => updatePayment(1, { mode: event.target.value as IpdBillPaymentInput['mode'] })}>{billSecondaryPaymentModes.map((mode) => <option key={mode}>{mode}</option>)}</select></label><label>Bank<select value={newPayments[1]?.bankName ?? ''} onChange={(event) => updatePayment(1, { bankName: event.target.value })}><option>Axis Bank</option><option>Canara Bank</option><option>State Bank</option></select></label><label>Amount<input type="number" min={0} step="0.01" value={newPayments[1]?.amount ?? 0} onChange={(event) => updatePayment(1, { amount: Number(event.target.value) })} /></label></div>
            </div>
          </div>
          <div className="opd-total-panel">
            <div><span>Sub Total</span><strong>{currency(subTotal)}</strong></div>
            <div><span>Miscellaneous</span><input type="number" min={0} step="0.01" value={miscCharge} onChange={(event) => setMiscCharge(Number(event.target.value))} /><select value={miscChargeType} onChange={(event) => setMiscChargeType(event.target.value as 'flat' | 'percentage')}><option value="flat">Rs.</option><option value="percentage">%</option></select></div>
            <div><span>Total</span><strong>{currency(total)}</strong></div>
            <div><span>Discount</span><input type="number" min={0} step="0.01" value={totalDiscount} onChange={(event) => setTotalDiscount(Number(event.target.value))} /><select value={discountType} onChange={(event) => setDiscountType(event.target.value as 'flat' | 'percentage')}><option value="flat">Rs.</option><option value="percentage">%</option></select></div>
            <div><span>Grand Total</span><strong>{currency(grandTotal)}</strong></div>
            <div><span>Total Payment</span><strong>{currency(totalPayment)}</strong></div>
            <div><span>Due / Credit</span><strong>{dueAmount > 0 ? `Due ${currency(dueAmount)}` : `Credit ${currency(creditAmount)}`}</strong></div>
          </div>
        </div>
      </section>

      <section className="opd-bill-subsection">
        <div className="opd-bill-subsection-head"><h3>Receipt List</h3><span>Total Payment <strong>{currency(existingPaid)}</strong></span></div>
        {bill.payments.length === 0 ? (
          <p className="opd-bill-empty-note">No payments recorded for this bill.</p>
        ) : (
          <div className="table-scroll">
            <table className="opd-bill-payment-table">
              <thead><tr><th>#</th><th>Date</th><th>Mode</th><th>Bank</th><th>Received By</th><th>Amount</th></tr></thead>
              <tbody>
                {bill.payments.map((payment, index) => (
                  <tr key={payment.id}>
                    <td>R{index + 1}</td>
                    <td>{formatIpdDateTime(payment.date)}</td>
                    <td>{payment.mode}</td>
                    <td>{payment.bankName ?? '-'}</td>
                    <td>{payment.createdBy.name}</td>
                    <td>{currency(payment.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="opd-edit-bill-footer-row">
        <div className="opd-edit-bill-remarks">
          <label><CkEditorField label="Finance Remarks" value={financeRemark} onChange={setFinanceRemark} /></label>
          <label><CkEditorField label="Party/Department Remarks" value={departmentPartyRemark} onChange={setDepartmentPartyRemark} /></label>
        </div>
      </div>

      <div className="opd-form-actions">
        <button className="button button-secondary opd-danger-outline" type="button" disabled={cancelBill.isPending} onClick={() => void confirmCancel()}>{cancelBill.isPending ? 'Cancelling…' : 'Full Bill Cancel'}</button>
        <button className="button button-primary" type="button" disabled={updateBill.isPending} onClick={submit}>{updateBill.isPending ? 'Saving…' : 'Update Bill'}</button>
      </div>
      <p className="opd-edit-bill-footer">Admission Dialysis No {admission.dialysisNumber}</p>
    </div>
  );
};
